// ** Third Party Imports
import * as yup from 'yup';

// ** Utils Imports
import FormUtil from '@utils/FormUtil';

const appendRequired = (
    required: boolean,
    yupObj: yup.AnySchema,
    fieldName: string,
    fieldLabel: string
) => {
    if (required) {
        return yupObj.required(FormUtil.msg.errRequired(fieldLabel));
    }
    return yupObj.notRequired();
};

const getStringOptional = () => yup.string().notRequired();

const getStringTrimmedScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    minLength: number,
    maxLength: number,
    regex?: RegExp
) => {
    const root = yup.string().trim().matches(regex!, `Invalid ${fieldLabel}`);

    let scheme = root;
    if (minLength > 1) {
        scheme = scheme.min(minLength, obj =>
            FormUtil.msg.errMin(fieldLabel, obj.value.length, obj.min)
        );
    }

    if (maxLength > 1) {
        scheme = scheme.max(maxLength, obj =>
            FormUtil.msg.errMax(fieldLabel, obj.value.length, obj.max)
        );
    }

    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getRichTextScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    minLength: number,
    maxLength: number
) => {
    const root = yup
        .string()
        .transform(value => {
            if (value == null) return '';
            return value
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim();
        })
        .test('rich-text-not-empty', `${fieldLabel} is required`, value => {
            if (!required) return true;
            if (value == null) return false;
            const clean = value
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim();
            return clean.length > 0;
        });

    let scheme = root;
    if (minLength > 0) {
        scheme = scheme.min(minLength, obj =>
            FormUtil.msg.errMin(fieldLabel, obj.value.length, obj.min)
        );
    }

    if (maxLength > 0) {
        scheme = scheme.max(maxLength, obj =>
            FormUtil.msg.errMax(fieldLabel, obj.value.length, obj.max)
        );
    }

    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getEmailScheme = (fieldName: string, fieldLabel: string, required: boolean) => {
    const root = yup
        .string()
        .email(FormUtil.msg.errFormat(fieldLabel))
        .max(40, obj => FormUtil.msg.errMax(fieldLabel, obj.value.length, obj.max));

    let scheme = root;
    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getStringDropdownScheme = (fieldName: string, fieldLabel: string, required: boolean) => {
    const root = yup.string();

    let scheme = root;
    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getStringArrayScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    minSelected: number,
    maxSelected: number,
    isFile?: boolean
) => {
    const root = yup.array();

    let scheme = root;
    if (minSelected > 0) {
        if (isFile) {
            scheme = scheme.min(minSelected, obj =>
                FormUtil.msg.errArrayMinFiles(fieldLabel, obj.min)
            );
        } else {
            scheme = scheme.min(minSelected, obj => FormUtil.msg.errArrayMin(fieldLabel, obj.min));
        }
    }

    if (maxSelected > 0) {
        if (isFile) {
            scheme = scheme.max(maxSelected, obj =>
                FormUtil.msg.errArrayMaxFiles(fieldLabel, obj.max)
            );
        } else {
            scheme = scheme.max(maxSelected, obj => FormUtil.msg.errArrayMax(fieldLabel, obj.max));
        }
    }

    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getNumberScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    minValue: number,
    maxValue: number
) => {
    const root = yup.number().typeError(FormUtil.msg.errFormatNumeric(fieldLabel));

    let scheme = root;
    if (minValue > 0) {
        scheme = scheme.min(minValue, obj => FormUtil.msg.errNumMin(fieldLabel, obj.min));
    }

    if (maxValue > 0) {
        scheme = scheme.max(maxValue, obj => FormUtil.msg.errNumMax(fieldLabel, obj.max));
    }

    scheme = appendRequired(required, scheme, fieldName, fieldLabel);

    return scheme;
};

const getCheckboxScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    errorMessage: string
) => {
    const root = yup.bool();

    let scheme = root;
    if (required) {
        scheme = scheme.oneOf([true], errorMessage);
    }

    return scheme;
};

const getUsernameScheme = (fieldName: string, fieldLabel: string, required: boolean) => {
    const root = yup
        .string()
        .trim()
        .lowercase()
        .min(5, obj => FormUtil.msg.errMin(fieldLabel, obj.value.length, obj.min))
        .max(20, obj => FormUtil.msg.errMax(fieldLabel, obj.value.length, obj.max));

    let scheme = null;
    if (required) {
        scheme = appendRequired(required, root, fieldName, fieldLabel);
    } else {
        scheme = yup.string().when(fieldName, val => {
            if (val?.length > 0) {
                return root;
            }
            return appendRequired(required, yup.string(), fieldName, fieldLabel);
        });
    }

    return scheme;
};

const getPasswordScheme = (fieldName: string, fieldLabel: string, required: boolean) => {
    const root = yup.string();

    let scheme = root;
    if (required) {
        scheme = appendRequired(required, root, fieldName, fieldLabel);
        scheme = scheme.min(8, obj => `${FormUtil.msg.errNumMin(fieldLabel, obj.min)} characters`);
    } else {
        scheme = yup.string().when(fieldName, val => {
            if (val?.length > 8) {
                return root;
            }
            return getStringOptional();
        });
    }

    return scheme;
};

const getConfirmPasswordScheme = (
    fieldName: string,
    fieldLabel: string,
    required: boolean,
    password1FieldName: string
) => {
    const root = yup.string().oneOf([yup.ref(password1FieldName)], FormUtil.msg.PASSWORD_UNMATCH);

    let scheme = null;
    if (required) {
        scheme = appendRequired(required, root, fieldName, fieldLabel);
    } else {
        scheme = yup.string().when(password1FieldName, val => {
            if (val?.length > 0) {
                return root;
            }
            return getStringOptional();
        });
    }

    return scheme;
};

const YupUtil = {
    getStringTrimmedScheme,
    getEmailScheme,
    getStringDropdownScheme,
    getStringArrayScheme,
    getNumberScheme,
    getCheckboxScheme,
    getUsernameScheme,
    getPasswordScheme,
    getConfirmPasswordScheme,
    getRichTextScheme,
};

export default YupUtil;
